import { LoaiPhieuNhapEnum } from "@/core/enums/loaiPhieuNhap.enum";
import { computed, unref, type MaybeRef } from "vue";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import { PHIEU_NHAP_KHO_LOAI } from "../constants";
import type { PhieuNhapKhoFormConfig, PhieuNhapKhoFormMode } from "../types";

const ROUTE_CONFIG: Record<
  string,
  Pick<
    PhieuNhapKhoFormConfig,
    | "loaiPhieu"
    | "mode"
    | "isHopDong"
    | "pageTitle"
    | "documentTitleSuffix"
    | "breadcrumbActionLabel"
    | "submitCreateMessage"
    | "submitUpdateMessage"
    | "confirmCreateTitle"
    | "confirmUpdateTitle"
  >
> = {
  "tao-phieu-nhap-kho-khac": {
    loaiPhieu: PHIEU_NHAP_KHO_LOAI.TAM_NHAP,
    mode: "create",
    isHopDong: false,
    pageTitle: "Tạo mới phiếu nhập khác",
    documentTitleSuffix: "Phiếu nhập khác",
    breadcrumbActionLabel: "Tạo phiếu nhập khác",
    submitCreateMessage: "Tạo phiếu nhập khác thành công",
    submitUpdateMessage: "Chỉnh sửa phiếu nhập thành công",
    confirmCreateTitle: "Bạn có chắc chắn muốn lưu phiếu nhập kho này không?",
    confirmUpdateTitle: "Bạn có chắc chắn muốn lưu phiếu nhập kho này không?",
  },
  "tao-moi-phieu-nhap-kho": {
    loaiPhieu: PHIEU_NHAP_KHO_LOAI.HOP_DONG_MUA_BAN,
    mode: "create",
    isHopDong: true,
    pageTitle: "Tạo mới phiếu nhập kho & biên bản kiểm nghiệm",
    documentTitleSuffix: "Phiếu nhập kho & biên bản kiểm nghiệm",
    breadcrumbActionLabel: "Tạo mới phiếu nhập kho & biên bản kiểm nghiệm",
    submitCreateMessage: "Tạo phiếu nhập kho & biên bản kiểm nghiệm thành công",
    submitUpdateMessage: "Vật tư phiếu nhập kho cập nhật thành công",
    confirmCreateTitle:
      "Tạo mới phiếu nhập kho & BBKN với những thông tin này không?",
    confirmUpdateTitle:
      "Bạn có chắc chắn muốn lưu chỉnh sửa cho phiếu nhập kho & biên bản kiểm nghiệm này không?",
  },
  "cap-nhat-phieu-nhap-kho": {
    loaiPhieu: null,
    mode: "edit",
    isHopDong: null,
    pageTitle: "Chỉnh sửa phiếu nhập kho",
    documentTitleSuffix: "Phiếu nhập kho",
    breadcrumbActionLabel: "Chỉnh sửa",
    submitCreateMessage: "",
    submitUpdateMessage: "Chỉnh sửa phiếu nhập thành công",
    confirmCreateTitle: "",
    confirmUpdateTitle:
      "Bạn có chắc chắn muốn lưu chỉnh sửa cho biên bản kiểm nghiệm này không?",
  },
};

export function resolvePhieuNhapKhoFormConfig(
  route: RouteLocationNormalizedLoaded,
  options?: {
    loaiPhieu?: LoaiPhieuNhapEnum | null;
    isHopDong?: boolean;
  }
): PhieuNhapKhoFormConfig {
  const routeName = route.name?.toString() ?? "";
  const base =
    ROUTE_CONFIG[routeName] ?? ROUTE_CONFIG["tao-phieu-nhap-kho-khac"];

  const isHopDong =
    options?.isHopDong ??
    (base.isHopDong === null ? null : Boolean(base.isHopDong));

  const loaiPhieu =
    options?.loaiPhieu ??
    base.loaiPhieu ??
    (isHopDong
      ? PHIEU_NHAP_KHO_LOAI.HOP_DONG_MUA_BAN
      : PHIEU_NHAP_KHO_LOAI.TAM_NHAP);

  let pageTitle = base.pageTitle;
  let breadcrumbActionLabel = base.breadcrumbActionLabel;

  if (base.mode === "edit" && isHopDong !== null) {
    pageTitle = isHopDong ? "Chỉnh sửa BBKN" : "Chỉnh sửa phiếu nhập khác";
    breadcrumbActionLabel = isHopDong ? "Sửa BBKN" : "Sửa phiếu nhập khác";
  }

  return {
    ...base,
    loaiPhieu,
    isHopDong,
    isTamNhap: isHopDong === false,
    pageTitle,
    breadcrumbActionLabel,
  };
}

export function usePhieuNhapKhoFormMode(
  route: RouteLocationNormalizedLoaded,
  options?: MaybeRef<{
    loaiPhieu?: LoaiPhieuNhapEnum | null;
    isHopDong?: boolean;
  }>
) {
  const formConfig = computed(() =>
    resolvePhieuNhapKhoFormConfig(route, unref(options))
  );

  return {
    formConfig,
  };
}

export function isPhieuNhapKhoEditMode(mode: PhieuNhapKhoFormMode) {
  return mode === "edit";
}
